import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Siren, Heart, Flame, Eye, X, ChevronRight, Trophy, Zap, Star, Shield } from 'lucide-react';
import { auth } from '../firebase';
import { Incident, GameRound, GameAnswer, GameRole, PlayerStats, ACHIEVEMENTS } from '../types';
import {
  createGameRound,
  subscribeToActiveRound,
  subscribeToRoundAnswers,
  subscribeToGameLeaderboard,
  submitAnswer,
  endRound,
  getMinutesSinceActivity,
  updateActivityTime,
} from '../services/gameService';

const ROUND_DURATION_MS = 12000;

const ROLE_CONFIG: Record<GameRole, { label: string; color: string; border: string; bg: string; icon: React.ReactNode }> = {
  OFFICER:     { label: 'OFFICER',     color: 'text-blue-400',   border: 'border-blue-600/50',   bg: 'bg-blue-900/20',   icon: <Siren size={14} /> },
  PARAMEDIC:   { label: 'PARAMEDIC',   color: 'text-red-400',    border: 'border-red-600/50',    bg: 'bg-red-900/20',    icon: <Heart size={14} /> },
  FIREFIGHTER: { label: 'FIREFIGHTER', color: 'text-orange-400', border: 'border-orange-600/50', bg: 'bg-orange-900/20', icon: <Flame size={14} /> },
  OBSERVER:    { label: 'OBSERVER',    color: 'text-amber-400',  border: 'border-amber-600/50',  bg: 'bg-amber-900/20',  icon: <Eye size={14} /> },
  CRIMINAL:    { label: 'CRIMINAL',    color: 'text-purple-400', border: 'border-purple-600/50', bg: 'bg-purple-900/30', icon: <Shield size={14} /> },
};

interface GamePanelProps {
  incidents: Incident[];
  userGeminiKey?: string;
  onClose: () => void;
  ghostProtocol?: boolean;
}

export default function GamePanel({ incidents, userGeminiKey, onClose, ghostProtocol = false }: GamePanelProps) {
  const [activeRound, setActiveRound]       = useState<GameRound | null>(null);
  const [answers, setAnswers]               = useState<GameAnswer[]>([]);
  const [selectedChoice, setSelectedChoice] = useState<number | null>(null);
  const [result, setResult]                 = useState<{ correct: boolean; points: number; achievements: string[] } | null>(null);
  const [timeLeft, setTimeLeft]             = useState(0);
  const [timeProgress, setTimeProgress]     = useState(1);
  const [playerRole, setPlayerRole]         = useState<GameRole>('OBSERVER');
  const [leaderboard, setLeaderboard]       = useState<PlayerStats[]>([]);
  const [tab, setTab]                       = useState<'game' | 'board'>('game');
  const [toastAchievements, setToastAchievements] = useState<string[]>([]);
  const [generatingFor, setGeneratingFor]   = useState<string | null>(null);
  const [showJackpot, setShowJackpot]       = useState(false);
  const [aryaBark, setAryaBark]             = useState(false);
  const [nightWatch, setNightWatch]         = useState(false);

  const timerRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const aryaRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const roundEndRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioRef    = useRef<AudioContext | null>(null);

  // ── Night Watch easter egg ──────────────────────────────────────────────────
  useEffect(() => {
    const h = new Date().getHours();
    setNightWatch(h >= 2 && h < 5);
  }, []);

  // ── Arya's Watch: if no incident for 22+ min, bark ─────────────────────────
  useEffect(() => {
    aryaRef.current = setInterval(() => {
      if (getMinutesSinceActivity() >= 22 && !aryaBark) {
        setAryaBark(true);
        playBark();
        setTimeout(() => setAryaBark(false), 4000);
      }
    }, 60000);
    return () => { if (aryaRef.current) clearInterval(aryaRef.current); };
  }, [aryaBark]);

  // ── Subscribe to active round ───────────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribeToActiveRound((round) => {
      if (round && round.id !== activeRound?.id) {
        setActiveRound(round);
        setSelectedChoice(null);
        setResult(null);
        setShowJackpot(round.jackpot);
        if (round.jackpot) setTimeout(() => setShowJackpot(false), 3000);
        updateActivityTime();
      } else if (!round && activeRound?.status === 'ACTIVE') {
        setActiveRound(prev => prev ? { ...prev, status: 'ENDED' } : null);
      }
    });
    return unsub;
  }, [activeRound?.id]);

  // ── Subscribe to answers for active round ──────────────────────────────────
  useEffect(() => {
    if (!activeRound) return;
    const unsub = subscribeToRoundAnswers(activeRound.id, setAnswers);
    return unsub;
  }, [activeRound?.id]);

  // ── Countdown timer ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeRound || activeRound.status !== 'ACTIVE') return;
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      const remaining = activeRound.endTime - Date.now();
      if (remaining <= 0) {
        setTimeLeft(0);
        setTimeProgress(0);
        clearInterval(timerRef.current!);
        // Auto-end round on client side
        if (activeRound.status === 'ACTIVE') {
          endRound(activeRound.id);
          setActiveRound(prev => prev ? { ...prev, status: 'ENDED' } : null);
        }
      } else {
        setTimeLeft(Math.ceil(remaining / 1000));
        setTimeProgress(remaining / ROUND_DURATION_MS);
      }
    }, 100);

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [activeRound?.id, activeRound?.status]);

  // ── Leaderboard subscription ────────────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribeToGameLeaderboard(setLeaderboard);
    return unsub;
  }, []);

  // ── Auto-generate round when new incident arrives ───────────────────────────
  useEffect(() => {
    if (!incidents.length || activeRound?.status === 'ACTIVE') return;
    const latest = incidents[0];
    if (!latest || generatingFor === latest.id) return;

    // Only generate if no active round and incident is fresh (< 5 min)
    const freshMs = (latest.freshnessMinutes || 99) * 60000;
    if (freshMs > 5 * 60000) return;

    // Check role filter — only generate if incident matches player's role or player is OBSERVER
    const targetRole = activeRound?.targetRole;
    if (targetRole && targetRole !== 'ALL' && targetRole !== playerRole && playerRole !== 'OBSERVER') return;

    setGeneratingFor(latest.id);
    updateActivityTime();

    createGameRound(latest, userGeminiKey).catch(e => {
      console.error('[GamePanel] Failed to create round:', e);
      setGeneratingFor(null);
    });
  }, [incidents[0]?.id]);

  // ── Manual round spawn (operator button) ───────────────────────────────────
  // The auto-spawn logic only fires for incidents < 5 min old. In a quiet
  // scanner window (no fresh dispatches) the game would sit idle forever, which
  // is the reason the game has historically never appeared to play. This button
  // forces a round using the freshest incident we have, regardless of age, and
  // falls back to a hand-crafted demo round if there are no incidents at all.
  const [spawning, setSpawning] = useState(false);
  const handleSpawnRound = useCallback(async () => {
    if (activeRound?.status === 'ACTIVE' || spawning) return;
    setSpawning(true);
    updateActivityTime();
    try {
      let incident: Incident | null = incidents[0] || null;
      // Demo fallback — synthetic incident so the operator can still see the
      // game spin up even on a dead scanner night.
      if (!incident) {
        incident = {
          id: 'demo-' + Date.now(),
          type: 'DOMESTIC DISTURBANCE',
          description: 'Caller reports loud argument and possible physical altercation at a residence. Units en route. Caller unable to confirm whether weapons are involved.',
          location: { lat: 32.2226, lng: -110.9747 }, // downtown Tucson
          threatLevel: 'MEDIUM' as any,
          freshnessMinutes: 1,
          timestamp: Date.now(),
          source: 'demo',
        } as any;
      }
      setGeneratingFor(incident!.id);
      const id = await createGameRound(incident!, userGeminiKey);
      if (!id) {
        console.warn('[GamePanel] Spawn returned null — Gemini key issue or Firestore write blocked');
      }
    } catch (e) {
      console.error('[GamePanel] Spawn failed:', e);
    } finally {
      setSpawning(false);
    }
  }, [activeRound?.status, spawning, incidents, userGeminiKey]);

  // ── Submit answer ───────────────────────────────────────────────────────────
  const handleChoice = useCallback(async (idx: number) => {
    if (!activeRound || selectedChoice !== null || activeRound.status !== 'ACTIVE') return;
    if (!auth.currentUser) return;

    const responseTimeMs = Date.now() - activeRound.startTime;
    setSelectedChoice(idx);

    const { pointsEarned, isCorrect, newAchievements } = await submitAnswer(
      activeRound,
      idx,
      responseTimeMs,
      answers,
      leaderboard.find(p => p.userId === auth.currentUser?.uid)?.currentStreak || 0
    );

    setResult({ correct: isCorrect, points: pointsEarned, achievements: newAchievements });

    if (newAchievements.length > 0) {
      setToastAchievements(newAchievements);
      setTimeout(() => setToastAchievements([]), 5000);
    }
  }, [activeRound, selectedChoice, answers, leaderboard]);

  // ── Bark SFX (Web Audio) ────────────────────────────────────────────────────
  function playBark() {
    try {
      if (!audioRef.current) audioRef.current = new AudioContext();
      const ctx = audioRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(); osc.stop(ctx.currentTime + 0.3);
      // Second bark
      setTimeout(() => {
        const osc2 = ctx.createOscillator();
        const g2 = ctx.createGain();
        osc2.connect(g2); g2.connect(ctx.destination);
        osc2.frequency.setValueAtTime(700, ctx.currentTime);
        osc2.frequency.exponentialRampToValueAtTime(180, ctx.currentTime + 0.12);
        g2.gain.setValueAtTime(0.25, ctx.currentTime);
        g2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
        osc2.start(); osc2.stop(ctx.currentTime + 0.25);
      }, 400);
    } catch(e) {}
  }

  // ── Computed values ─────────────────────────────────────────────────────────
  const myAnswer    = answers.find(a => a.userId === auth.currentUser?.uid);
  const hasAnswered = !!myAnswer || selectedChoice !== null;
  const roundEnded  = activeRound?.status === 'ENDED';
  const myStats     = leaderboard.find(p => p.userId === auth.currentUser?.uid);
  const isNight     = new Date().getHours() >= 2 && new Date().getHours() < 5;

  const timerColor = timeProgress > 0.5 ? '#22c55e' : timeProgress > 0.25 ? '#f59e0b' : '#ef4444';

  // ── Role matches this round? ────────────────────────────────────────────────
  const roundMatchesRole = !activeRound
    || activeRound.targetRole === 'ALL'
    || activeRound.targetRole === playerRole
    || playerRole === 'OBSERVER';

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className={`flex flex-col h-full font-mono ${nightWatch && isNight ? 'border-red-900/30' : ''}`}>

      {/* Jackpot flash */}
      <AnimatePresence>
        {showJackpot && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.2 }}
            className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <div className="text-4xl font-orbitron font-black text-amber-400 uppercase tracking-widest
                            drop-shadow-[0_0_30px_rgba(245,158,11,0.9)] animate-pulse">
              🎰 JACKPOT<br/>
              <span className="text-2xl text-amber-300">CRITICAL — 3× POINTS</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Arya easter egg */}
      <AnimatePresence>
        {aryaBark && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute bottom-16 right-4 z-40 text-xs text-amber-400 font-mono italic"
          >
            🐕 *woof woof*
          </motion.div>
        )}
      </AnimatePresence>

      {/* Achievement toasts */}
      <div className="absolute top-2 right-2 z-40 flex flex-col gap-2">
        <AnimatePresence>
          {toastAchievements.map((id) => {
            const a = ACHIEVEMENTS[id];
            return a ? (
              <motion.div
                key={id}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                className="bg-amber-900/90 border border-amber-600/60 rounded-sm px-3 py-2 text-xs flex items-center gap-2 shadow-lg"
              >
                <span className="text-lg">{a.icon}</span>
                <div>
                  <div className="text-amber-400 font-bold uppercase tracking-wider">{a.name}</div>
                  <div className="text-amber-400">{a.description}</div>
                </div>
              </motion.div>
            ) : null;
          })}
        </AnimatePresence>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-amber-900/30 bg-black/60 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-amber-500" />
          <span className="text-xs font-bold text-amber-500 uppercase tracking-widest font-orbitron">
            TACTICAL GAME
          </span>
          {nightWatch && isNight && (
            <span className="text-[9px] text-red-400 border border-red-900/50 px-1 rounded-sm animate-pulse">
              🌙 NIGHT WATCH
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {myStats && (
            <div className="flex items-center gap-1 text-xs text-amber-600">
              <Star size={10} className="text-amber-500" />
              <span className="text-amber-400 font-bold">{myStats.gameScore.toLocaleString()}</span>
              {myStats.currentStreak >= 3 && (
                <span className="text-orange-400 ml-1">🔥{myStats.currentStreak}</span>
              )}
            </div>
          )}
          <div className="flex gap-1">
            <button
              onClick={() => setTab('game')}
              className={`px-2 py-0.5 text-[9px] uppercase rounded-sm transition-colors ${tab === 'game' ? 'bg-amber-600 text-black' : 'text-amber-400 hover:text-amber-400'}`}
            >Game</button>
            <button
              onClick={() => setTab('board')}
              className={`px-2 py-0.5 text-[9px] uppercase rounded-sm transition-colors ${tab === 'board' ? 'bg-amber-600 text-black' : 'text-amber-400 hover:text-amber-400'}`}
            >Board</button>
          </div>
          <button onClick={onClose} className="text-amber-500 hover:text-amber-500">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Role selector */}
      <div className="flex gap-1 px-3 py-2 border-b border-amber-900/20 flex-shrink-0 bg-black/40">
        {(Object.keys(ROLE_CONFIG) as GameRole[])
          .filter(role => role !== 'CRIMINAL' || ghostProtocol)
          .map(role => {
          const r = ROLE_CONFIG[role];
          return (
            <button
              key={role}
              onClick={() => setPlayerRole(role)}
              className={`flex items-center gap-1 px-2 py-1 text-[9px] uppercase rounded-sm border transition-all
                ${playerRole === role
                  ? `${r.bg} ${r.border} ${r.color} font-bold`
                  : 'border-transparent text-amber-500 hover:text-amber-500'
                }
                ${role === 'CRIMINAL' ? 'ring-1 ring-purple-800/40' : ''}
              `}
            >
              {r.icon}
              <span className="hidden sm:inline">{r.label}</span>
            </button>
          );
        })}
        <div className="flex-1" />
        <div className="text-[9px] text-amber-500 self-center">Your role ↑</div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">

        {/* ── GAME TAB ── */}
        {tab === 'game' && (
          <div className="p-3 flex flex-col gap-3">
            {!activeRound && (
              <div className="text-center py-6 text-amber-500 text-xs">
                <div className="text-2xl mb-2">📡</div>
                <div className="uppercase tracking-widest">Monitoring for incidents...</div>
                <div className="text-[10px] mt-1 text-amber-300 mb-4">
                  Rounds auto-spawn on fresh (&lt;5 min) scanner incidents.
                  Press SPAWN below to force a round right now using the most recent incident.
                </div>
                <button
                  onClick={handleSpawnRound}
                  disabled={spawning || !!generatingFor}
                  className="mx-auto px-4 py-2 border border-amber-500/60 bg-amber-900/30 text-amber-300 hover:bg-amber-800/40 hover:border-amber-400 rounded-sm text-[11px] uppercase tracking-widest font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-orbitron"
                  title="Force-generate a tactical question right now. Uses the most recent incident in your feed; falls back to a demo prompt if the scanner is quiet."
                >
                  {spawning || generatingFor ? '⚙ GENERATING…' : '▶ SPAWN ROUND'}
                </button>
                <div className="text-[9px] mt-3 text-amber-700 italic">
                  {incidents.length > 0
                    ? `${incidents.length} incident${incidents.length === 1 ? '' : 's'} in feed — spawning will use "${incidents[0].type}"`
                    : 'No incidents in feed — spawn will use a demo prompt'}
                </div>
              </div>
            )}

            {activeRound && (
              <>
                {/* Incident context */}
                <div className={`border rounded-sm p-2 text-[10px] ${
                  activeRound.jackpot
                    ? 'border-amber-500/50 bg-amber-900/20'
                    : 'border-amber-900/30 bg-black/40'
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`uppercase tracking-widest font-bold text-[9px] ${
                      activeRound.jackpot ? 'text-amber-400' : 'text-amber-400'
                    }`}>
                      {activeRound.jackpot && '🎰 '}{activeRound.incidentType}
                    </span>
                    <span className={`text-[9px] px-1 rounded-sm border ${
                      activeRound.targetRole === 'ALL'
                        ? 'text-amber-600 border-amber-900/50'
                        : `${ROLE_CONFIG[activeRound.targetRole as GameRole]?.color || 'text-amber-400'} ${ROLE_CONFIG[activeRound.targetRole as GameRole]?.border || 'border-amber-700/50'}`
                    }`}>
                      {activeRound.targetRole === 'ALL' ? 'ALL ROLES' : activeRound.targetRole}
                    </span>
                  </div>
                  <p className="text-amber-600 leading-relaxed">{activeRound.incidentDescription.slice(0, 120)}</p>
                </div>

                {/* Timer ring + question */}
                <div className="relative">
                  <div className="flex items-start gap-3">
                    {/* Countdown ring */}
                    {activeRound.status === 'ACTIVE' && !hasAnswered && (
                      <div className="flex-shrink-0 relative w-10 h-10">
                        <svg width="40" height="40" className="-rotate-90">
                          <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(245,158,11,0.15)" strokeWidth="3"/>
                          <circle
                            cx="20" cy="20" r="16" fill="none"
                            stroke={timerColor}
                            strokeWidth="3"
                            strokeDasharray={`${2 * Math.PI * 16}`}
                            strokeDashoffset={`${2 * Math.PI * 16 * (1 - timeProgress)}`}
                            strokeLinecap="round"
                            style={{ transition: 'stroke-dashoffset 0.1s linear, stroke 0.3s' }}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold"
                             style={{ color: timerColor }}>
                          {timeLeft}
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-amber-300 leading-relaxed font-medium flex-1">
                      {activeRound.question}
                    </p>
                  </div>
                </div>

                {/* Choices */}
                {!roundMatchesRole && !hasAnswered && activeRound.status === 'ACTIVE' && (
                  <div className="text-[10px] text-amber-500 text-center py-2 border border-amber-900/20 rounded-sm">
                    This round is for {activeRound.targetRole} — switch roles or wait as Observer (lower points)
                  </div>
                )}

                <div className="grid grid-cols-1 gap-2">
                  {activeRound.choices.map((choice, idx) => {
                    let btnClass = 'border-amber-900/30 text-amber-600 hover:border-amber-600/60 hover:text-amber-400 hover:bg-amber-900/10';

                    if (hasAnswered || roundEnded) {
                      if (idx === activeRound.correctIndex) {
                        btnClass = 'border-green-600/70 bg-green-900/25 text-green-400';
                      } else if (idx === selectedChoice && idx !== activeRound.correctIndex) {
                        btnClass = 'border-red-600/70 bg-red-900/20 text-red-400';
                      } else {
                        btnClass = 'border-amber-900/20 text-amber-500 opacity-50';
                      }
                    }

                    return (
                      <motion.button
                        key={idx}
                        whileHover={!hasAnswered && activeRound.status === 'ACTIVE' ? { scale: 1.01 } : {}}
                        whileTap={!hasAnswered && activeRound.status === 'ACTIVE' ? { scale: 0.99 } : {}}
                        onClick={() => handleChoice(idx)}
                        disabled={hasAnswered || activeRound.status !== 'ACTIVE'}
                        className={`text-left px-3 py-2 border rounded-sm text-xs transition-all ${btnClass} disabled:cursor-default`}
                      >
                        <span className="text-amber-500 mr-2">{String.fromCharCode(65 + idx)}.</span>
                        {choice}
                        {(hasAnswered || roundEnded) && idx === activeRound.correctIndex && (
                          <span className="ml-2 text-green-500">✓</span>
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                {/* Result */}
                <AnimatePresence>
                  {result && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`border rounded-sm p-2 text-xs ${
                        result.correct
                          ? playerRole === 'CRIMINAL'
                            ? 'border-purple-600/60 bg-purple-900/20 text-purple-300'
                            : 'border-green-700/60 bg-green-900/20 text-green-400'
                          : playerRole === 'CRIMINAL'
                            ? 'border-red-700/60 bg-red-900/20 text-red-400'
                            : 'border-red-700/60 bg-red-900/15 text-red-400'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold uppercase tracking-wider">
                          {playerRole === 'CRIMINAL'
                            ? result.correct ? '🕶️ EVADED' : '🚨 CAUGHT'
                            : result.correct ? '✓ CORRECT' : '✗ WRONG'
                          }
                        </span>
                        {result.correct && result.points > 0 && (
                          <span className="text-amber-400 font-bold">+{result.points} pts</span>
                        )}
                      </div>
                      <p className="text-[10px] leading-relaxed opacity-80">
                        {playerRole === 'CRIMINAL'
                          ? result.correct
                            ? 'Ghost move — cops went the other way.'
                            : `Busted. Cops had it right: ${activeRound.explanation}`
                          : activeRound.explanation
                        }
                      </p>
                    </motion.div>
                  )}

                  {(hasAnswered || roundEnded) && !result && myAnswer && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border border-amber-900/30 rounded-sm p-2 text-[10px] text-amber-400"
                    >
                      {activeRound.explanation}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Answers count */}
                {answers.length > 0 && (
                  <div className="flex items-center gap-2 text-[9px] text-amber-500">
                    <span>{answers.length} player{answers.length !== 1 ? 's' : ''} answered</span>
                    <span>·</span>
                    <span className="text-green-700">{answers.filter(a => a.isCorrect).length} correct</span>
                    {answers.find(a => a.isFirst && a.isCorrect) && (
                      <>
                        <span>·</span>
                        <span className="text-amber-600">📡 {answers.find(a => a.isFirst && a.isCorrect)?.displayName} dispatched</span>
                      </>
                    )}
                  </div>
                )}

                {/* Round ended — waiting */}
                {roundEnded && (
                  <div className="text-center text-[10px] text-amber-500 py-1">
                    Round ended · Next question incoming...
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── LEADERBOARD TAB ── */}
        {tab === 'board' && (
          <div className="p-3">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={14} className="text-amber-500" />
              <span className="text-xs font-bold text-amber-500 uppercase tracking-widest">Top Operators</span>
            </div>
            {leaderboard.length === 0 && (
              <div className="text-amber-500 text-xs text-center py-6">No scores yet</div>
            )}
            <div className="space-y-1">
              {leaderboard.map((player, idx) => (
                <div
                  key={player.userId}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs border transition-colors
                    ${player.userId === auth.currentUser?.uid
                      ? 'border-amber-600/40 bg-amber-900/15'
                      : 'border-transparent hover:border-amber-900/30'
                    }`}
                >
                  <span className={`w-5 text-right text-[10px] font-bold ${
                    idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-slate-400' : idx === 2 ? 'text-amber-400' : 'text-amber-300'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="flex-1 text-amber-500 truncate">{ghostProtocol && player.userId === auth.currentUser?.uid ? '👁 GHOST' : player.displayName}</span>
                  {player.currentStreak >= 3 && <span className="text-[9px] text-orange-400">🔥{player.currentStreak}</span>}
                  <span className="text-amber-400 font-bold font-mono">{player.gameScore.toLocaleString()}</span>
                  <div className="flex gap-0.5">
                    {(player.achievements || []).slice(0, 3).map(id => (
                      <span key={id} title={ACHIEVEMENTS[id]?.name} className="text-[10px]">
                        {ACHIEVEMENTS[id]?.icon}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* My stats */}
            {myStats && (
              <div className="mt-4 border border-amber-900/30 rounded-sm p-3 text-[10px] space-y-1">
                <div className="text-amber-600 uppercase tracking-wider font-bold mb-2">Your Stats</div>
                <div className="flex justify-between text-amber-400">
                  <span>Total Score</span><span className="text-amber-400 font-bold">{myStats.gameScore.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-amber-400">
                  <span>Accuracy</span>
                  <span className="text-amber-400">
                    {myStats.totalAnswers > 0
                      ? `${Math.round((myStats.correctAnswers / myStats.totalAnswers) * 100)}%`
                      : '—'
                    }
                  </span>
                </div>
                <div className="flex justify-between text-amber-400">
                  <span>Best Streak</span><span className="text-orange-400">{myStats.bestStreak} 🔥</span>
                </div>
                <div className="flex justify-between text-amber-400">
                  <span>Questions</span><span className="text-amber-400">{myStats.totalAnswers}</span>
                </div>
                {(myStats.achievements || []).length > 0 && (
                  <div className="pt-1">
                    <div className="text-amber-500 mb-1">Achievements</div>
                    <div className="flex flex-wrap gap-1">
                      {(myStats.achievements || []).map(id => {
                        const a = ACHIEVEMENTS[id];
                        return a ? (
                          <span key={id} title={`${a.name}: ${a.description}`} className="text-base cursor-help">
                            {a.icon}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
